/** The open canvas: its top bar, side islands, tab strip, zoom pill, presence, onboarding and import flow. */
export const canvas = {
  /* tools (src/lib/tools.ts) */
  toolSelect: 'Select',
  toolHand: 'Hand',
  toolFrame: 'Frame',
  toolRectangle: 'Rectangle',
  toolCircle: 'Circle',
  toolPath: 'Vector path',
  toolText: 'Text',
  toolComment: 'Comment',

  /* top bar */
  untitled: 'Untitled',
  allCanvases: 'All canvases',
  readOnlyBadgeTitle: 'Your share link lets you look around but not change the design',
  canComment: 'Can comment',
  viewOnly: 'View only',
  canvasIdTitle: 'Canvas id — agents use this with the MCP tools',
  autoSaved: '— Auto-saved',
  reconnecting: '— Reconnecting…',
  justYou: 'Just you here',
  importTitle: 'Import a live web page as a frame',
  import: 'Import',
  exportTitle: 'Export frames as a PDF',
  export: 'Export',
  share: 'Share',
  presentFromFrame: 'Present from this frame',
  presentPrototype: 'Present the prototype',
  addFrameToPresent: 'Add a frame to present',
  presentPrototypeLabel: 'Present prototype',
  connectAi: 'Connect AI',
  connectAiShort: 'AI',
  canvasActions: 'Canvas actions',

  /* page toasts and notices */
  pasteImageFailed: 'Couldn’t paste image — {error}',
  dropImageFailed: 'Couldn’t add the dropped image — {error}',
  uiHidden: 'UI hidden · {key}. to show',
  memorySuggestionReview: 'Memory suggestion — review',
  hideForNow: 'Hide for now',
  openMemory: 'Open Memory',
  savedToMemory: 'Saved to Memory',
  linkCopied: 'Canvas link copied',
  itemsImported: { one: '{count} item imported', other: '{count} items imported' },
  importedWithFailures: '{imported} · {failed} failed',
  cardsQueued: { one: '{count} card queued — Doop is on it', other: '{count} cards queued — Doop is on it' },
  appUpdated: 'doop was updated',
  reload: 'Reload',

  /* canvas not found */
  notFoundTitle: 'Canvas not found',
  notFoundBody: '“{id}” doesn’t match a canvas we know about. It may have been deleted, or the link may be wrong.',
  backToCanvases: 'Back to your canvases',

  /* panels */
  resizeLayers: 'Resize Layers panel',
  resizeAgents: 'Resize Agents panel',
  canvasActionsDescription: 'Layers, import, export, share, Prototype mode, or your account settings.',
  layers: 'Layers',
  importWebsite: 'Import website',
  shareCanvas: 'Share canvas',
  prototypeMode: 'Prototype mode',
  leavePrototypeMode: 'Leave Prototype mode',
  frameInspector: 'Frame inspector',
  canvasActivity: 'Canvas activity',

  /* side rail */
  canvasPanels: 'Canvas panels',
  inspectFrame: 'Inspect frame',
  help: 'Help',
  docs: 'Docs',
  contactSupport: 'Contact support',
  youAre: 'You are “{name}” — click to change your name',
  changeDisplayName: 'Change your display name',
  agents: 'Agents · ⌘K to ask the Doop Agent',
  agentsWorking: 'Agents · {working} working · ⌘K to ask the Doop Agent',
  comments: 'Comments',
  commentsOpen: 'Comments · {open} open',
  activity: 'Activity',
  memory: 'Memory',
  memorySuggestion: 'Memory · suggestion to review',

  /* desktop tab strip */
  openCanvases: 'Open canvases',
  closeTab: 'Close tab',
  newCanvas: 'New canvas',
  copyLink: 'Copy link',
  openInBrowser: 'Open in browser',
  closeOtherTabs: 'Close other tabs',
  closeAllTabs: 'Close all tabs',

  /* zoom */
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  zoomLevel: 'Zoom level',
  zoomToFit: 'Zoom to fit',
  zoomToSelection: 'Zoom to selection',
  zoomTo: 'Zoom to {percent}%',
  showRulers: 'Show rulers',
  hideRulers: 'Hide rulers',

  /* onboarding */
  gettingStarted: 'Getting started',
  gettingStartedDescription: 'Two steps to bring your own agent onto the canvas.',
  stepConnect: 'Connect your own agent',
  stepConnectHint: 'Claude Code, Codex, Cursor or any MCP client can design on this canvas as you.',
  stepTask: 'Give it a task',
  copiedLower: 'copied',
  copyStarterPrompt: 'copy a starter prompt',
  stepTaskHint: "Paste it into your agent's chat and watch the frame stream in.",
  loopDone:
    "That's the loop. One more trick: hover a task in the panel and reply to it — your note becomes an open request any agent picks up mid-flight.",
  gotIt: 'Got it',
  dismiss: 'Dismiss',
  dismissChecklist: 'Dismiss checklist',

  /* presence */
  agentOf: "{owner}'s agent",
  agent: 'agent',
  followAround: 'Follow {name} around the canvas',
  stopFollowing: 'Stop following {name}',
  stopFollowingEsc: 'Stop following (Esc)',
  goWhereWorking: '{label} — click to go where it is working',
  online: '{online} online',
  following: 'Following',

  /* rename yourself */
  displayNameTitle: 'Your display name',
  displayNameLede: 'Shown on your cursor, in the activity feed, and on everything you leave for an agent.',
  name: 'Name',
  saving: 'Saving…',
  saveName: 'Save name',

  /* import: shared */
  reviewBeforeImport: 'Review before import',
  selectAll: 'Select all',
  clear: 'Clear',
  importing: 'Importing…',
  optional: 'Optional',

  /* import: repository review */
  repoTitle: 'Import the design system',
  repoFramework: 'A {framework} app.',
  repoLede:
    "Doop distills the repo's design system into a style guide pinned to this canvas — every agent follows it from then on. Each component or page you pick becomes a card on the board: Doop sketches it from the source and lands it as a frame. Whole pages are optional.",
  extractTitle: 'Extract the design system into a style guide',
  extractDescription:
    "Palette, type and spacing from the repo's theme — pinned to the canvas, followed by every agent.",
  screensToImport: 'Screens to import',
  kindComponent: 'component',
  kindStory: 'story',
  laneStatic: 'from repo',
  lanePlaceholder: 'agent sketch',
  hidePages: 'hide pages',
  showPages: { one: 'show {count} page (optional)', other: 'show {count} pages (optional)' },
  repoTruncated: 'The repository listing was cut short — very large repos show a subset.',
  repoImporting: 'Doop is importing — the style guide and sketches fill in on the canvas…',
  repoImport: 'Import {items}',
  repoPieceSystem: 'design system',
  repoPieceComponents: { one: '{count} component', other: '{count} components' },
  repoPieceScreens: { one: '{count} screen', other: '{count} screens' },
  errScreensGone: 'Those screens are no longer in the repository — re-run the scan',
  errAlreadyOnBoard: 'Everything you picked is already on the board',
  errRepoImportFailed: 'repository import failed',

  /* import: website */
  websiteTitle: 'Import a website',
  websiteLede: 'Bring in one page, or discover a whole site and choose the pages you want before anything is added.',
  websiteUrl: 'Website URL',
  wholeSiteTitle: 'Import the entire website',
  wholeSiteDescription: 'Find public pages on the same site, then review the list.',
  websiteNote: 'Snapshots stay editable and commentable. Scripts are removed.',
  findingPages: 'Finding pages…',
  discoverPages: 'Discover pages',
  importPage: 'Import page',
  choosePages: 'Choose pages',
  pagesFound: {
    one: '{count} page found. Everything is selected by default; uncheck anything you don’t need.',
    other: '{count} pages found. Everything is selected by default; uncheck anything you don’t need.',
  },
  pagesToImport: 'Pages to import',
  pagesTruncated: 'Showing the first 100 pages found. Narrow the starting URL if needed.',
  capturingPages: 'Capturing {count} pages — larger sites can take a few minutes.',
  importingCount: 'Importing {count}…',
  importPages: { one: 'Import {count} page', other: 'Import {count} pages' },
  errImportFailed: 'import failed',
  errDiscoveryFailed: 'page discovery failed',
  errNoPages: 'No pages could be imported',
  errNoPagesReason: 'No pages could be imported — {reason}',
  errWebsiteImportFailed: 'website import failed',

  /* import: live-app sync */
  syncTitle: 'Sync a live app',
  syncForbidden: 'Sync keys are for canvas members — ask the owner for durable access.',
  syncLede:
    'For apps a crawler cannot reach: behind a login, a VPN, or on localhost. Paste one script tag and each screen people visit lands here as a frame, imported once.',
  screens: { one: '{count} screen', other: '{count} screens' },
  synced: 'synced',
  neverSynced: 'never synced',
  revokeKey: 'Revoke this key',
  copiedBang: 'Copied!',
  appName: 'App name',
  appNamePlaceholder: 'Admin dashboard',
  createKey: 'Create key',
  syncStep1Title: 'Get a key for this canvas',
  syncStep1Detail: 'The key only writes here. Revoke it from this screen at any time.',
  syncStep2Title: 'Paste the script tag',
  syncStep2Detail: 'One script tag in your app’s layout. Nothing else to install.',
  syncStep3Title: 'Browse the app',
  syncStep3Detail: 'Every new screen someone visits lands as a frame. Delete a frame to re-import it fresh.',
  syncNote:
    'Visible page content uploads to Doop as static HTML. Input values are dropped; mark anything sensitive with data-doop-mask.',

  /* import: GitHub */
  githubTitle: 'Connect a GitHub repo',
  githubForbidden: 'Repo connections are for canvas members — ask the owner for durable access.',
  githubLede:
    'Doop reads the repo’s routing conventions and lists its screens for review. Nothing lands until you pick.',
  nothingImported: 'nothing imported yet',
  analyzing: 'Analyzing…',
  analyze: 'Analyze',
  disconnectRepo: 'Disconnect this repository',
  pickRepo: 'Pick a repository to connect to this canvas',
  private: 'private',
  connecting: 'Connecting…',
  connect: 'Connect',
  allConnected: 'All installed repositories are connected.',
  openingGithub: 'Opening GitHub…',
  connectGithub: 'Connect GitHub',
  pasteToken: 'paste a token instead',
  repoPlaceholder: 'owner/repository',
  tokenPlaceholder: 'Fine-grained token (github_pat_…)',
  githubNoteApp: 'Access is scoped to the repos you pick and revocable on GitHub.',
  githubNoteToken: 'A fine-grained, read-only token — it never leaves the server.',
  errListRepos: 'could not list the installed repositories',
  errStartInstall: 'could not start the GitHub install',
  errConnectRepo: 'could not connect the repository',
  errAnalysis: 'repository analysis failed',
} as const
